/**
 * Parses a single line as a Markdown-style list item (bullet or numbered).
 * Intentionally avoids regex so static analysis cannot flag ReDoS risk.
 *
 * Mirrors: /^(\s*)(?:([-*])|(\d+)\.)\s+(.*)$/
 */
export type ChatListLineMatch =
  | { kind: 'bullet'; indent: string; content: string }
  | { kind: 'numbered'; indent: string; number: string; content: string };

function isAsciiDigit(c: string): boolean {
  return c >= '0' && c <= '9';
}

function isWhitespaceChar(c: string): boolean {
  return /\s/.test(c);
}

export function parseChatListLine(line: string): ChatListLineMatch | null {
  let i = 0;
  const len = line.length;
  while (i < len && isWhitespaceChar(line[i])) {
    i += 1;
  }
  const indent = line.slice(0, i);
  if (i >= len) {
    return null;
  }

  const c = line[i];
  if (c === '-' || c === '*') {
    i += 1;
    if (i >= len || !isWhitespaceChar(line[i])) {
      return null;
    }
    while (i < len && isWhitespaceChar(line[i])) {
      i += 1;
    }
    return { kind: 'bullet', indent, content: line.slice(i) };
  }

  if (isAsciiDigit(c)) {
    const numStart = i;
    while (i < len && isAsciiDigit(line[i])) {
      i += 1;
    }
    const number = line.slice(numStart, i);
    if (i >= len || line[i] !== '.') {
      return null;
    }
    i += 1;
    if (i >= len || !isWhitespaceChar(line[i])) {
      return null;
    }
    while (i < len && isWhitespaceChar(line[i])) {
      i += 1;
    }
    return { kind: 'numbered', indent, number, content: line.slice(i) };
  }

  return null;
}
